import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { History, ArrowDownCircle, ArrowUpCircle, Filter, Download as DownloadIcon, ChevronLeft, ChevronRight } from 'lucide-react';

const TransactionHistoryPage = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(1);
    const ITEMS_PER_PAGE = 20;

    useEffect(() => {
        fetchTransactions();
    }, [filter, page]);

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const offset = (page - 1) * ITEMS_PER_PAGE;
            let url = `/wallet/transactions?limit=${ITEMS_PER_PAGE}&offset=${offset}`;
            if (filter !== 'all') {
                url += `&type=${filter}`;
            }

            const res = await api.get(url);
            setTransactions(res.data.transactions || []);
        } catch (error) {
            console.error('Failed to load transactions', error);
        } finally {
            setLoading(false);
        }
    };

    const getTypeLabel = (type) => {
        const labels = {
            deposit: 'Deposit',
            withdraw: 'Withdrawal',
            transfer_in: 'Transfer In',
            transfer_out: 'Transfer Out',
            internal_transfer: 'Internal Transfer',
            trade: 'Trade',
            bot_profit: 'Bot Profit',
            bot_subscription: 'Bot Fee',
            bot_investment: 'Bot Invest'
        };
        return labels[type] || type.replace(/_/g, ' ');
    };

    return (
        <div className="bg-[#0b0e11] min-h-screen text-gray-100 font-sans selection:bg-blue-500/30">
            <Navbar />

            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                            <History className="text-blue-500" size={32} />
                            Transaction History
                        </h1>
                        <p className="text-gray-400 mt-2">View and manage your entire financial history</p>
                    </div>

                    {/* Export Button (Placeholder for now) */}
                    <button className="flex items-center gap-2 bg-[#1e2329] border border-gray-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-700 transition">
                        <DownloadIcon size={16} />
                        Export CSV
                    </button>
                </div>

                <div className="bg-[#181a20] rounded-3xl border border-gray-800 shadow-xl overflow-hidden">
                    {/* Filters */}
                    <div className="p-6 border-b border-gray-800 overflow-x-auto">
                        <div className="flex gap-2">
                            {['all', 'deposit', 'withdraw', 'transfer_in', 'transfer_out', 'trade', 'bot_profit'].map(f => (
                                <button
                                    key={f}
                                    onClick={() => { setFilter(f); setPage(1); }}
                                    className={`px-4 py-2 rounded-xl text-sm font-bold capitalize transition whitespace-nowrap ${filter === f
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-[#0b0e11] text-gray-400 hover:text-white hover:bg-gray-700'
                                        }`}
                                >
                                    {f.replace('_', ' ')}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto min-h-[500px]">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                                <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                                Loading...
                            </div>
                        ) : transactions.length > 0 ? (
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="bg-[#0b0e11]/50 text-gray-500 text-xs font-bold uppercase tracking-wider">
                                        <th className="px-6 py-4">Type</th>
                                        <th className="px-6 py-4">Asset</th>
                                        <th className="px-6 py-4 text-right">Amount</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4">Date</th>
                                        <th className="px-6 py-4">Details</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800">
                                    {transactions.map(tx => {
                                        const isIncoming = ['deposit', 'transfer_in', 'bot_profit', 'trade_buy'].includes(tx.type) || (tx.type === 'internal_transfer' && tx.notes?.includes('Received'));

                                        return (
                                            <tr key={tx.id} className="hover:bg-white/[0.02] transition">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isIncoming ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                                                            }`}>
                                                            {isIncoming ? <ArrowDownCircle size={16} /> : <ArrowUpCircle size={16} />}
                                                        </div>
                                                        <span className="font-medium text-white">{getTypeLabel(tx.type)}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 font-bold text-gray-300">
                                                    {tx.token?.symbol || 'USDT'}
                                                </td>
                                                <td className={`px-6 py-4 text-right font-mono font-bold ${isIncoming ? 'text-green-500' : 'text-red-500'}`}>
                                                    {isIncoming ? '+' : '-'}{parseFloat(tx.amount).toFixed(8)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-2 py-1 rounded-md text-xs font-bold uppercase ${tx.status === 'completed' ? 'bg-green-500/10 text-green-500' :
                                                            tx.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' :
                                                                'bg-red-500/10 text-red-500'
                                                        }`}>
                                                        {tx.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-gray-400 text-sm">
                                                    {new Date(tx.createdAt).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 text-gray-500 text-sm max-w-[200px] truncate">
                                                    {tx.txHash ? (
                                                        <span title={tx.txHash}>{tx.txHash.substring(0, 10)}...</span>
                                                    ) : (
                                                        tx.notes || '-'
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                                <History size={48} className="mb-4 opacity-50" />
                                <p>No transactions found</p>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    <div className="p-4 border-t border-gray-800 flex justify-between items-center bg-[#0b0e11]/30">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50 transition"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <span className="text-gray-500 text-sm font-mono">Page {page}</span>
                        <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={transactions.length < ITEMS_PER_PAGE}
                            className="p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50 transition"
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionHistoryPage;
